import React, { useEffect, useState } from "react";
import Table, { Row, Data } from "../../components/ui/table/table";

import { useQuery } from "@tanstack/react-query";

import { formatAmount, getAmountWithCurrency } from "../../utils/utils";
import PageWrapper from "../../layouts/PageWrapper";
import Section from "../../layouts/Section";

import { Input, Select } from "../../components/form/formControls";
import { StatusBadge } from "../../components/StatusBadge";

import SubUserSelector from "./SubUserSelector";

import { useSelector } from "react-redux";
import { useMemo } from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { fetchReportLedger } from "../../api/wallet-service";
import { downloadReportLedger } from "../../utils/downloadReport";
import { Button } from "../../components/ui/buttons/Button";
import { APP_USERS } from "../../consts/AppRoles";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";

import { useLocale } from "../../hooks/useLocale";

const ReportsCardLoad = () => {
  // globals
  const inputDate = useSelector((s) => s.global.inputDate);
  const currentUser = useSelector((s) => s.auth.currentUser);

  // user selected from the sub user list
  const selectedUser = useSelector((s) => s.reports.selectedUser);
  const { page } = usePagination();

  // Current Selcted Item
  const [currentRecord, setCurrentRecord] = useState(null);
  const [isDownoadingReport, setIsDownoadingReport] = useState(false);

  const { localize } = useLocale(["reports"]);

  // Handling Filters
  const filterSchema = {
    status: "",
    moxeyId: "",
    closingBalance: "",
    orderId: "",
    retrievalRefNo: "",
    internalReferenceNo: "",
    remarks: "",
    requestedBy: "",
    approvedBy: "",
    transactionId: "",
    transactionType: "",
    voucherId: "",
  };
  const [filters, setFilters] = useState(filterSchema);
  const handleFilter = (filteredValues) => setFilters(filteredValues);
  const debouncedHandleFilters = useDebounce(handleFilter);

  const reportQuery = useQuery(
    ["reports/ledger", filters, inputDate, page, selectedUser?.userHashId],
    () =>
      fetchReportLedger({
        userHashId: selectedUser?.userHashId,
        dateRange: inputDate.date,
        filters,
        page,
      }),
    {
      enabled: !!selectedUser,
    }
  );

  // download report
  const handleDownloadReport = () => {
    setIsDownoadingReport(true);
    downloadReportLedger({
      userHashId: selectedUser?.userHashId,
      dateRange: inputDate.date,
      email: currentUser.email,
    })
      .then((res) => {
        setIsDownoadingReport(false);
      })
      .catch((err) => {});
  };

  return (
    <>
      <PageWrapper>
        {/* Page Header */}
        <Section>
          <div className="d-flex">
            <h3>{localize("heading")}</h3>
            <div className="ms-auto">
              <SubUserSelector
                clientHashId={currentUser.productType === APP_USERS.CLIENT}
              />
            </div>
          </div>
        </Section>

        {/* Table Data */}
        <Section>
          <>
            <div className="d-flex justify-content-between align-items-center mt-4">
              <h5 className="text-muted">{localize("ledger.heading")}</h5>
              <Button
                text={localize("ledger.actions.downloadReport")}
                loading={isDownoadingReport}
                className="btn btn-primary"
                onClick={handleDownloadReport}
              />
            </div>

            {/* Table Data */}
            <Table
              loading={reportQuery.isLoading}
              cols={localize("ledger.table.cols", { returnObjects: true })}
              dataSize={reportQuery?.data?.prefundList?.length}
              filters={
                <TableFilters
                  filterSchema={filters}
                  handleFilter={debouncedHandleFilters}
                />
              }
            >
              {/* =============== Display Data from API =============*/}
              {reportQuery?.data?.prefundList?.map((item) => (
                <>
                  <Row>
                    {/* Status */}
                    <Data>
                      <StatusBadge status={item.status} />
                    </Data>
                    <Data>
                      {item.dateOfTransaction ? item.dateOfTransaction : "-"}
                    </Data>
                    <Data>{item.transactionId ? item.transactionId : "-"}</Data>
                    <Data>
                      {item.retrievalRefNo ? item.retrievalRefNo : "-"}
                    </Data>
                    <Data>
                      {item.internalReferenceNo
                        ? item.internalReferenceNo
                        : "-"}
                    </Data>
                    <Data>{item.orderId ? item.orderId : "-"}</Data>
                    <Data>{item.voucherId ? item.voucherId : "-"}</Data>
                    <Data>
                      {item.transactionAmount
                        ? getAmountWithCurrency(
                            formatAmount(item.transactionAmount),
                            "AED"
                          )
                        : "-"}
                    </Data>
                    <Data>
                      {item.transactionType ? item.transactionType : "-"}
                    </Data>
                    <Data>
                      {item.credit
                        ? getAmountWithCurrency(
                            formatAmount(item.transactionAmount),
                            "AED"
                          )
                        : "-"}
                    </Data>

                    <Data>
                      {item.debit
                        ? getAmountWithCurrency(
                            formatAmount(item.transactionAmount),
                            "AED"
                          )
                        : "-"}
                    </Data>
                    <Data>
                      {item.requestedAmount
                        ? getAmountWithCurrency(
                            formatAmount(item.requestedAmount),
                            "AED"
                          )
                        : "-"}
                    </Data>

                    <Data>{item.feeType ? item.feeType : "-"}</Data>

                    <Data>
                      {item.feeFixed
                        ? `${item.feeValue} FLAT`
                        : `${item.feeValue} %`}
                    </Data>

                    <Data>
                      {item.feeDeductValue ? item.feeDeductValue : "-"}
                    </Data>
                    <Data>
                      {item.closingBalance
                        ? getAmountWithCurrency(
                            formatAmount(item.closingBalance),
                            "AED"
                          )
                        : "-"}
                    </Data>
                    <Data>{item.requestedBy ? item.requestedBy : "-"}</Data>
                    <Data>{item.approvedBy ? item.approvedBy : "-"}</Data>
                    <Data>{item.driverName ? item.driverName : "-"}</Data>
                    <Data>
                      {item.driverMobileNo ? item.driverMobileNo : "-"}
                    </Data>
                    <Data>{item.remarks ? item.remarks : "-"}</Data>
                  </Row>
                </>
              ))}
              {/* show empty list */}
            </Table>

            <div className="d-flex justify-content-end mb-5">
              <Pagination
                show={reportQuery.data?.prefundList?.length}
                totalPages={reportQuery.data?.pagination?.totalPages}
                handlePageSelect={(page) =>
                  setFilters({ ...filters, page: page })
                }
              />
            </div>
          </>
        </Section>
      </PageWrapper>
    </>
  );
};

const TableFilters = ({ filterSchema, handleFilter }) => {
  {
    /**
     *
     * Note : "name" attribute is important for filter controls
     * Interpreted as : [name] and filterSchema.name
     *
     */
  }
  const statusOptions = [
    {
      value: "Approved",
      text: "Approved",
    },
  ];

  const transactionTypeOptions = [
    {
      value: "CardLoad_debit",
      text: "Card Load Debit",
    },
    {
      value: "Client_Prefund",
      text: "Client Prefund",
    },
    {
      value: "Client_Prefund_Debit",
      text: "Client Prefund Debit",
    },
    {
      value: "WALLET_DEBIT",
      text: "Wallet Debit",
    },
    {
      value: "WALLET_CREDIT",
      text: "Wallet Credit",
    },
    {
      value: "Internal_Client_Prefund",
      text: "Internal Client Prefund",
    },
    {
      value: "Broker_Card_Load_Debit",
      text: "Broker Card Load Debit",
    },
    {
      value: "Vendor_Payment_Debit",
      text: "Vendor Payment Debit",
    },
    {
      value: "Vendor_Payment_Credit",
      text: "Vendor Payment Credit",
    },
    {
      value: "MOXEY_STORE_T_DEBIT",
      text: "Moxey Store Debit",
    },
  ];

  // table filter has its own state for persisting the filter values
  const [filteredValues, setFilteredValues] = useState(filterSchema);

  const handleFilteredValues = (event) => {
    const { name, value } = event.target;
    setFilteredValues({ ...filteredValues, [name]: value });
    handleFilter({ ...filteredValues, [name]: value, page: 0 });
  };

  return (
    <>
      <tr>
        <td>
          <Select
            name="status"
            value={filteredValues.status}
            onChange={(event) => handleFilteredValues(event)}
          >
            <option value="" selected>
              Select status
            </option>
            {statusOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </td>
        <td></td>
        <td>
          <Input
            name="transactionId"
            value={filteredValues.transactionId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="retrievalRefNo"
            value={filteredValues.retrievalRefNo}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="internalReferenceNo"
            value={filteredValues.internalReferenceNo}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="orderId"
            value={filteredValues.orderId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="voucherId"
            value={filteredValues.voucherId}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td>
          <Select
            name="transactionType"
            value={filteredValues.transactionType}
            onChange={(event) => handleFilteredValues(event)}
          >
            <option value="" selected>
              Select status
            </option>
            {transactionTypeOptions.map((option) => (
              <option value={option.value}>{option.text}</option>
            ))}
          </Select>
        </td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td>
          <Input
            name="closingBalance"
            value={filteredValues.closingBalance}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="requestedBy"
            value={filteredValues.requestedBy}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td>
          <Input
            name="approvedBy"
            value={filteredValues.approvedBy}
            onInput={(event) => handleFilteredValues(event)}
          />
        </td>
        <td></td>
        <td></td>
        <td></td>
      </tr>
    </>
  );
};

export default ReportsCardLoad;
